"use client";

import { useState, useEffect } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress,
  Divider, Drawer, FormControl, Grid, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DownloadIcon from "@mui/icons-material/Download";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const pad = (n) => String(n).padStart(2, "0");
const nowStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function ExpenseManager() {
  const token = useSelector((state) => state.loginSlice.token);

  const [expenses, setExpenses]     = useState([]);
  const [totalAmt, setTotalAmt]     = useState(0);
  const [loading, setLoading]       = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // pagination state
  const [page, setPage]           = useState(0);   // MUI TablePagination is 0-indexed
  const rowsPerPage = 20;
  const [totalRecords, setTotalRecords] = useState(0);

  // filter state
  const [filterFrom, setFilterFrom]               = useState("");
  const [filterTo, setFilterTo]                   = useState("");
  const [filterPaymentType, setFilterPaymentType] = useState("");
  const [filterName, setFilterName]               = useState("");

  // form state
  const [form, setForm]     = useState({ name: "", reason: "", amount: "", date: nowStr(), remarks: "", payment_type: "CASH" });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");
  const [csvLoading, setCsvLoading] = useState(false);

  const [activeAccounts, setActiveAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [employees, setEmployees] = useState([]);

  const fetchActiveAccounts = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/accounts`, token);
      setActiveAccounts(res?.data?.data || []);
    } catch { /* ignore */ }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/employees`, token);
      const list = Array.isArray(res?.data) ? res.data : [];
      setEmployees(list.filter((e) => e.status !== false));
    } catch { /* ignore */ }
  };

  const fetchExpenses = async (currentPage = page, currentSize = rowsPerPage) => {
    setLoading(true);
    try {
      const params = [`page=${currentPage + 1}`, `size=${currentSize}`];
      if (filterFrom) params.push(`date_from=${new Date(filterFrom).toISOString()}`);
      if (filterTo)   params.push(`date_to=${new Date(filterTo).toISOString()}`);
      if (filterPaymentType) params.push(`payment_type=${filterPaymentType}`);
      if (filterName) params.push(`name=${encodeURIComponent(filterName)}`);

      const res = await axiosRequest("GET", `${apiBaseUrl}/expense?${params.join("&")}`, token);
      if (res?.data?.status) {
        setExpenses(res.data.data.list || []);
        setTotalAmt(res.data.data.total || 0);
        setTotalRecords(res.data.data.pagination?.totalRecords || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); fetchActiveAccounts(); fetchEmployees(); }, []);
const handleSearch = () => {
    setPage(0);
    fetchExpenses(0, rowsPerPage);
  };

  const handlePageChange = (_e, newPage) => {
    setPage(newPage);
    fetchExpenses(newPage, rowsPerPage);
  };

  const handleExportCsv = async () => {
    setCsvLoading(true);
    try {
      let url = `${apiBaseUrl}/expense/export-csv?`;
      if (filterFrom) url += `&date_from=${new Date(filterFrom).toISOString()}`;
      if (filterTo)   url += `&date_to=${new Date(filterTo).toISOString()}`;
      if (filterPaymentType) url += `&payment_type=${filterPaymentType}`;
      if (filterName) url += `&name=${encodeURIComponent(filterName)}`;
      const res = await fetch(url, { headers: { token } });
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = `expenses_${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(href);
    } catch (err) { console.error(err); }
    finally { setCsvLoading(false); }
  };

  const handleReset = () => {
    setFilterFrom(""); setFilterTo(""); setFilterPaymentType(""); setFilterName("");
    setPage(0);
    setTimeout(() => fetchExpenses(0, rowsPerPage), 0);
  };

  const handleOpenDrawer = () => {
    setForm({ name: "", reason: "", amount: "", date: nowStr(), remarks: "", payment_type: "CASH" });
    setSelectedAccount("");
    setFormError("");
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.name.trim())             return setFormError("Recipient name is required.");
    if (!form.reason.trim())           return setFormError("Reason is required.");
    if (!form.amount || Number(form.amount) <= 0) return setFormError("Enter a valid amount.");
    if (form.payment_type === "OTHER" && !selectedAccount)
      return setFormError("Please select an account for OTHER payment type.");

    setSaving(true);
    try {
      const payload = {
        name:          form.name.trim(),
        reason:        form.reason.trim(),
        amount:        Number(form.amount),
        denominations: [],
        date:          form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
        payment_type:  form.payment_type,
        remarks:       form.remarks.trim(),
      };
      if (form.payment_type === "OTHER" && selectedAccount) payload.account_id = selectedAccount;
      const res = await axiosRequest("POST", `${apiBaseUrl}/expense`, token, payload);

      if (res?.data?.status === false || res?.status === false) {
        setFormError(res?.data?.message || res?.message || "Failed to save expense.");
        return;
      }
      setDrawerOpen(false);
      fetchExpenses();
    } catch (e) {
      setFormError("Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>

      {/* PAGE HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptLongIcon color="error" fontSize="large" />
          <Typography variant="h4" fontWeight={700}>Expense Manager</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" color="error" startIcon={<DownloadIcon />} onClick={handleExportCsv} disabled={csvLoading}>
            {csvLoading ? "Exporting..." : "Export CSV"}
          </Button>
          <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={handleOpenDrawer}>
            Add Expense
          </Button>
        </Box>
      </Box>

      {/* FILTER BAR */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="datetime-local" label="From"
              InputLabelProps={{ shrink: true }}
              value={filterFrom}
              onChange={(e) => setFilterFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="datetime-local" label="To"
              InputLabelProps={{ shrink: true }}
              value={filterTo}
              disabled={!filterFrom}
              inputProps={{ min: filterFrom || "" }}
              onChange={(e) => setFilterTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={filterPaymentType}
                onChange={(e) => setFilterPaymentType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CASH">CASH</MenuItem>
                <MenuItem value="BANK">BANK</MenuItem>
                <MenuItem value="OTHER">OTHER</MenuItem>
                <MenuItem value="PETTY_EXPENSE">PETTY EXPENSE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth size="small" label="Recipient Name"
              placeholder="Search recipient..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button fullWidth variant="contained" color="success" onClick={handleSearch}>Search</Button>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button fullWidth variant="outlined" color="error" onClick={handleReset}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* SUMMARY BANNER */}
      <Paper sx={{
        p: 2, mb: 3, borderRadius: 2,
        background: "linear-gradient(135deg,#b71c1c,#e53935)",
        color: "#fff",
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
      }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Expenses</Typography>
          <Typography variant="h6" fontWeight={700}>{totalRecords} records</Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Amount</Typography>
          <Typography variant="h4" fontWeight={800}>{Number(totalAmt).toLocaleString("en-IN")}</Typography>
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#1c2433" }}>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Denomination Breakup</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cashier</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No expenses found
                    </TableCell>
                  </TableRow>
                ) : expenses.map((exp, i) => (
                  <TableRow key={exp._id} hover>
                    <TableCell align="center">{page * rowsPerPage + i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{exp.name}</TableCell>
                    <TableCell>{exp.reason}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${Number(exp.amount).toLocaleString("en-IN")}`}
                        color="error" size="small" sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={exp.payment_type === "PETTY_EXPENSE" ? "PETTY" : (exp.payment_type || "CASH")}
                        color={exp.payment_type === "BANK" ? "primary" : exp.payment_type === "OTHER" ? "warning" : exp.payment_type === "PETTY_EXPENSE" ? "secondary" : "default"}
                        size="small" sx={{ fontWeight: 700, minWidth: 52 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {(exp.denominations || []).map((d) => (
                          <Chip
                            key={d.denomination}
                            label={`${d.denomination}×${d.quantity}`}
                            size="small" variant="outlined"
                            sx={{ fontSize: 11 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{exp.created_by?.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{exp.created_by?.email || ""}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {exp.date ? new Date(exp.date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{exp.remarks || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[20]}
            />
          </>
        )}
      </Paper>

      {/* ADD EXPENSE DRAWER */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 480, p: 3, overflowY: "auto", height: "100%" }}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <ReceiptLongIcon color="error" />
            <Typography variant="h6" fontWeight={700}>New Expense Entry</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Record cash given to office staff or for general expenses
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth size="small"
                options={employees}
                getOptionLabel={(opt) => opt.name || ""}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={employees.find((e) => e.name === form.name) || null}
                onChange={(_, newValue) => setForm({ ...form, name: newValue?.name || "" })}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    {opt.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({opt.designation})</Typography>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Recipient Name *" placeholder="Select employee" />}
                noOptionsText="No employees found — add them in Employee Manager"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Reason *"
                placeholder="e.g. Tea & Snacks, Stationery, Cleaning"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" type="datetime-local" label="Date & Time"
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Type *</InputLabel>
                <Select
                  label="Payment Type *"
                  value={form.payment_type}
                  onChange={(e) => setForm({ ...form, payment_type: e.target.value })}
                >
                  <MenuItem value="CASH">CASH</MenuItem>
                  <MenuItem value="BANK">BANK</MenuItem>
                  <MenuItem value="OTHER">OTHER</MenuItem>
                  <MenuItem value="PETTY_EXPENSE">PETTY EXPENSE</MenuItem>
                </Select>
              </FormControl>
              {form.payment_type === "PETTY_EXPENSE" && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This amount will be added to the <b>Petty Fund</b> balance and counted as a cash expense on the counter.
                </Alert>
              )}
            </Grid>

            {form.payment_type === "OTHER" && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Select Account *</InputLabel>
                  <Select
                    label="Select Account *"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                  >
                    {activeAccounts.map((acc) => (
                      <MenuItem key={acc._id} value={acc._id}>
                        {acc.name}{acc.phone ? ` — ${acc.phone}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" type="number" label="Amount () *"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Remarks (optional)"
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              />
            </Grid>

{formError && (
              <Grid item xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setDrawerOpen(false)}>Cancel</Button>
                <Button
                  variant="contained" color="error"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {saving ? "Saving..." : "Save Expense"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    </Box>
  );
}
